// src/messaging/MessageItem.jsx
// メッセージ1件（吹き出し）【2026-10-16】
//   ・お知らせ（system）は中央に小さく
//   ・取り消し →「メッセージの送信を取り消しました」、直したもの →「編集済み」
//   ・返信先の引用、@の強調、リアクション（押すと付け外し。長押し・マウスを乗せると誰が押したか）
//   ・1対1だけ、自分のメッセージの下に「既読」（other_read_id 以下）。先生とのやり取り・グループには出さない
import React, { useEffect, useRef, useState } from 'react';
import { msgApi, REACTIONS } from './api';
import { clock } from './util';
import { AuthImage, FileChip } from './ui';
import MsgIcon from './MsgIcon';

const EDIT_WINDOW_MS = 24 * 3600 * 1000;

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/** 本文の中の「@名前」を強調する（改行はそのまま CSS の pre-wrap で出す） */
function renderText(text, mentions) {
  const names = (mentions || []).map(m => m.name).filter(Boolean);
  if (!names.length) return text;
  const re = new RegExp('(' + names.map(n => '@' + escapeRe(n)).join('|') + ')', 'g');
  return text.split(re).map((part, i) => (i % 2 === 1 ? <span key={i} className="msg-mention">{part}</span> : part));
}

function ReactionChip({ r, messageId, onToggle, disabled }) {
  const [who, setWho] = useState(null);   // null = 閉じている / [] = 読み込み済み
  const timer = useRef(null);
  const longPressed = useRef(false);
  const show = async () => {
    const res = await msgApi.get(`/messages/${messageId}/reactions`);
    if (res.success) setWho(res.reactions.filter(x => x.emoji === r.emoji).map(x => x.user.name));
  };
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => clear, []);
  return (
    <span className="msg-reaction-wrap"
          onMouseEnter={() => { clear(); timer.current = setTimeout(show, 450); }}
          onMouseLeave={() => { clear(); setWho(null); }}>
      <button type="button" className={'msg-reaction' + (r.mine ? ' mine' : '')} disabled={disabled}
              onClick={() => { if (longPressed.current) { longPressed.current = false; return; } onToggle(r.emoji, r.mine); }}
              onTouchStart={() => { clear(); longPressed.current = false; timer.current = setTimeout(() => { longPressed.current = true; show(); }, 500); }}
              onTouchEnd={clear}
              onContextMenu={(e) => { e.preventDefault(); show(); }}
              aria-label={`${r.emoji} ${r.count}人${r.mine ? '（あなたも押しています）' : ''}`}>
        <span>{r.emoji}</span><span className="msg-reaction-count">{r.count}</span>
      </button>
      {who && (
        <span className="msg-reaction-who" onClick={() => setWho(null)}>
          {who.length ? who.join('、') : '（表示できる人はいません）'}
        </span>
      )}
    </span>
  );
}

export default function MessageItem({
  msg, kind, otherReadId, canWrite, highlight,
  onReply, onEdit, onRetract, onReport, onBlock, onReact, onJumpTo, onError,
}) {
  const [actions, setActions] = useState(false);   // タップで操作を出す（スマホ）
  const [menu, setMenu] = useState(false);
  const [picker, setPicker] = useState(false);
  const ref = useRef(null);

  // 外を押したら閉じる
  useEffect(() => {
    if (!menu && !picker && !actions) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) { setMenu(false); setPicker(false); setActions(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [menu, picker, actions]);

  if (msg.kind === 'system') {
    return <div className="msg-system" data-mid={msg.id}>{msg.text || ''}</div>;
  }

  const mine = !!msg.mine;
  const retracted = !!msg.retracted;
  const canEdit = mine && !retracted && canWrite && Date.now() - new Date(msg.created_at).getTime() < EDIT_WINDOW_MS;
  const canReact = !retracted && canWrite;
  const canBlockSender = !mine && msg.sender && (kind === 'direct' || kind === 'group' || kind === 'class_group');
  const showRead = kind === 'direct' && mine && !retracted && otherReadId != null && msg.id <= otherReadId;

  const closeAll = () => { setMenu(false); setPicker(false); setActions(false); };

  return (
    <div ref={ref} data-mid={msg.id}
         className={'msg-row' + (mine ? ' mine' : '') + (actions ? ' show-actions' : '') + (highlight ? ' flash' : '')}>
      <div className="msg-col">
        {!mine && msg.sender && kind !== 'direct' && <div className="msg-sender">{msg.sender.name}</div>}
        {!mine && !msg.sender && <div className="msg-sender">（退会したユーザー）</div>}
        <div className="msg-bubble-line">
          <div className={'msg-bubble' + (retracted ? ' retracted' : '') + (msg.mentions_me ? ' mention-me' : '')}
               onClick={(e) => { if (e.target.closest('button,img,a')) return; setActions(a => !a); }}>
            {retracted ? (
              <span className="msg-retracted">メッセージの送信を取り消しました</span>
            ) : (
              <>
                {msg.reply_to && (
                  <button type="button" className="msg-quote" onClick={() => !msg.reply_to.unavailable && onJumpTo(msg.reply_to.id)}>
                    {msg.reply_to.unavailable ? '表示できないメッセージ'
                      : msg.reply_to.retracted ? '取り消されたメッセージ'
                        : <><strong>{msg.reply_to.sender ? msg.reply_to.sender.name : ''}</strong>{msg.reply_to.text}</>}
                  </button>
                )}
                {msg.image && <AuthImage url={msg.image.url} className="msg-bubble-img" />}
                {msg.file && <FileChip file={msg.file} onError={onError} />}
                {msg.text && <div className="msg-text">{renderText(msg.text, msg.mentions)}</div>}
              </>
            )}
          </div>
          {!retracted && (
            <div className="msg-actions">
              {canReact && (
                <button type="button" className="msg-icon-btn sm" aria-label="リアクション"
                        onClick={() => { setPicker(p => !p); setMenu(false); }}><MsgIcon name="smile" size={16} /></button>
              )}
              <button type="button" className="msg-icon-btn sm" aria-label="そのほかの操作"
                      onClick={() => { setMenu(m => !m); setPicker(false); }}><MsgIcon name="more" size={16} /></button>
              {picker && (
                <div className="msg-popover msg-emoji-picker">
                  {REACTIONS.map(e => (
                    <button type="button" key={e} onClick={() => { closeAll(); onReact(msg, e, (msg.reactions || []).some(r => r.mine && r.emoji === e)); }}>{e}</button>
                  ))}
                </div>
              )}
              {menu && (
                <div className="msg-popover msg-menu">
                  {canWrite && <button type="button" onClick={() => { closeAll(); onReply(msg); }}>返信</button>}
                  {msg.text && (
                    <button type="button" onClick={() => {
                      closeAll();
                      try { navigator.clipboard.writeText(msg.text); } catch (_) { /* コピーできない端末 */ }
                    }}>本文をコピー</button>
                  )}
                  {canEdit && <button type="button" onClick={() => { closeAll(); onEdit(msg); }}>編集</button>}
                  {mine && canWrite && <button type="button" className="danger" onClick={() => { closeAll(); onRetract(msg); }}>送信を取り消す</button>}
                  {!mine && <button type="button" className="danger" onClick={() => { closeAll(); onReport(msg); }}>通報する</button>}
                  {canBlockSender && <button type="button" className="danger" onClick={() => { closeAll(); onBlock(msg.sender); }}>{msg.sender.name}さんをブロック</button>}
                </div>
              )}
            </div>
          )}
        </div>
        {!retracted && msg.reactions && msg.reactions.length > 0 && (
          <div className="msg-reactions">
            {msg.reactions.map(r => (
              <ReactionChip key={r.emoji} r={r} messageId={msg.id} disabled={!canWrite}
                            onToggle={(emoji, isMine) => onReact(msg, emoji, isMine)} />
            ))}
          </div>
        )}
        <div className="msg-meta">
          {showRead && <span className="msg-read">既読</span>}
          {msg.edited && !retracted && <span>編集済み</span>}
          <span>{clock(msg.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
